<template>
  <v-dialog v-model="open" max-width="520">
    <v-card>
      <v-card-title>Test profile</v-card-title>
      <v-card-text>
        <p class="text-medium-emphasis mb-4">
          Temporarily give
          <strong>{{ authStore.user?.email ?? "this user" }}</strong> a
          different ADP job title and/or location to see which documents they
          would get. This only lasts until you stop impersonating; their real
          account and ADP record aren't changed. Leave a field empty to use
          their real value.
        </p>

        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-3"
        >
          {{ error }}
        </v-alert>
        <v-alert
          v-if="!loading && noOptions"
          type="info"
          variant="tonal"
          density="compact"
          class="mb-3"
        >
          No ADP data has been synced yet, so there are no job titles or
          locations to choose from.
        </v-alert>

        <v-autocomplete
          v-model="jobTitle"
          :items="options.jobTitles"
          label="Job title"
          placeholder="Real job title"
          persistent-placeholder
          clearable
          :loading="loading"
          :disabled="busy"
        />
        <v-autocomplete
          v-model="locationCode"
          :items="options.locations"
          item-title="name"
          item-value="code"
          label="Location"
          placeholder="Real location"
          persistent-placeholder
          clearable
          :loading="loading"
          :disabled="busy"
        />
      </v-card-text>
      <v-card-actions class="px-4 pb-4">
        <v-btn
          v-if="authStore.testProfile"
          variant="text"
          :disabled="busy"
          @click="apply(null, null)"
        >
          Clear
        </v-btn>
        <v-spacer />
        <v-btn :disabled="busy" @click="open = false">Cancel</v-btn>
        <v-btn
          color="primary"
          :loading="busy"
          @click="apply(jobTitle, locationCode)"
        >
          Apply
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { authApi, type TestProfileOptions } from "@/api/auth";
import { useAuthStore } from "@/stores/auth";

const props = defineProps<{ modelValue: boolean }>();
const emit = defineEmits<{ "update:modelValue": [value: boolean] }>();

const open = computed({
  get: () => props.modelValue,
  set: (v) => emit("update:modelValue", v),
});

const authStore = useAuthStore();

const options = ref<TestProfileOptions>({ jobTitles: [], locations: [] });
const jobTitle = ref<string | null>(null);
const locationCode = ref<string | null>(null);
const loading = ref(false);
const busy = ref(false);
const error = ref<string | null>(null);

const noOptions = computed(
  () => !options.value.jobTitles.length && !options.value.locations.length,
);

watch(
  () => props.modelValue,
  async (isOpen) => {
    if (!isOpen) return;
    error.value = null;
    jobTitle.value = authStore.testProfile?.jobTitle ?? null;
    locationCode.value = authStore.testProfile?.locationCode ?? null;
    loading.value = true;
    try {
      options.value = await authApi.getTestProfileOptions();
    } catch {
      error.value = "Failed to load ADP job titles and locations.";
    } finally {
      loading.value = false;
    }
  },
);

async function apply(title: string | null, location: string | null) {
  busy.value = true;
  error.value = null;
  try {
    await authStore.setTestProfile({
      jobTitle: title,
      locationCode: location,
    });
    open.value = false;
  } catch {
    error.value = "Failed to apply test profile.";
  } finally {
    busy.value = false;
  }
}
</script>
